import {Box} from '@mui/material';
import React, {useRef, useState, useEffect, useMemo} from 'react';

interface Props {
  progress: number;
  layout?: {
    barWidth: number;
    barHeight: number;
    spaceBetweenBar: number;
  };
  unfilledColor?: string;
}

const interpolateColor = (start: number[], end: number[], t: number) => {
  return [
    Math.round(start[0] + (end[0] - start[0]) * t),
    Math.round(start[1] + (end[1] - start[1]) * t),
    Math.round(start[2] + (end[2] - start[2]) * t),
  ];
};

const StripeProgressBar: React.FC<Props> = ({
  progress,
  layout = {barWidth: 2, spaceBetweenBar: 1, barHeight: 24},
  unfilledColor = '#344660', // default surface/subtle color
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const handleWindowResizeEvent = () => {
      // Update container's width on window resize event and regenerate progress chart
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth); // Get the actual width of the parent container
      }
    };

    // Set initial width
    handleWindowResizeEvent();

    // Add event listener for window resize event listen
    window.addEventListener('resize', handleWindowResizeEvent);

    return () => {
      window.removeEventListener('resize', handleWindowResizeEvent);
    };
  }, []);

  const stripes = useMemo(() => {
    if (containerWidth > 0 && progress !== undefined) {
      const stripeWidth = layout.barWidth; // Width of each stripe
      const spaceBetweenStripes = layout.spaceBetweenBar; // Space between stripes
      const totalStripeWidth = stripeWidth + spaceBetweenStripes;
      const totalStripes = Math.floor(containerWidth / totalStripeWidth);
      const filledStripesCount = Math.floor(totalStripes * (progress / 100)); // filled based on percentage
      const unfilledStripesCount = totalStripes - filledStripesCount;

      const c1 = [0, 141, 149]; // #008D95
      const c2 = [82, 101, 255]; // #5265FF
      const c3 = [118, 76, 182]; // #764CB6

      const colors: string[] = [];

      for (let i = 0; i < filledStripesCount; i++) {
        const t = i / (filledStripesCount - 1);

        let rgb;

        if (t < 0.5) {
          // first half: c1 → c2
          rgb = interpolateColor(c1, c2, t * 2);
        } else {
          // second half: c2 → c3
          rgb = interpolateColor(c2, c3, (t - 0.5) * 2);
        }

        colors.push(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
      }

      for (let i = 0; i < unfilledStripesCount; i++) {
        colors.push(unfilledColor); // surface/subtle (#344660)
      }
      return colors;
    }
    return [];
  }, [containerWidth, progress, layout, unfilledColor]);

  return (
    <Box
      role="presentation"
      ref={containerRef}
      display="flex"
      sx={{height: layout.barHeight}}
    >
      {stripes.map((color, i) => (
        <Box
          key={i}
          style={{
            backgroundColor: color,
            width: layout.barWidth,
            height: layout.barHeight,
            marginRight: 1,
          }}
        />
      ))}
    </Box>
  );
};

export default StripeProgressBar;
